import type { CognitoUserInfo, Group, IUser, Location, Role, UsersResponse } from '../../API/user'
import { UserApi } from '../../API/user'
import type { FormInstance, TableColumnsType } from 'antd'
import {
  Alert,
  Button,
  Card,
  Divider,
  Dropdown,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag
} from 'antd'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CloseOutlined,
  EditOutlined,
  EllipsisOutlined,
  LockOutlined,
  SearchOutlined,
  SyncOutlined,
  UnlockOutlined,
  UserAddOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'react-use'

import $api from '../../API'
import { ValidationError } from '../vendors/errors/validation-error'
import { useAppSelector } from '../../store'
import { roleColorMap } from '../../domain/role.enum'

type QueryContextKey = [
  'users',
  State['search'],
  State['filters'],
  State['pagination'],
  State['sort']
]

type MutationError = ValidationError | Error

type MutationPayload = Partial<
  Pick<
    IUser,
    | 'id'
    | 'email'
    | 'first_name'
    | 'last_name'
    | 'locked_since'
    | 'phone_number'
    | 'authentication_method'
  >
> & {
  roles?: string[]
  locations?: string[]
  groups?: string[]
}

const MFA_PHONE_ERROR_MESSAGE =
  'MFA requires a phone number. Select Passkey & UbiKey or enter a phone number.'

const locationColorMapper: Record<string, string> = {
  '003': 'blue',
  '001': 'red',
  '002': 'green',
  '004': 'purple'
}

interface Option {
  label: string
  value: string
}

interface FormValues {
  email?: string
  first_name?: string
  last_name?: string
  phone_number?: string
  authentication_method?: IUser['authentication_method']

  roles?: Option[]
  locations?: Option[]
  groups?: Option[]
}

type SortField = 'email' | 'first_name' | 'last_name' | 'roles' | 'status'
type SortOrder = 'asc' | 'desc'

interface State {
  pagination: {
    currentPage: number
    pageSize: number
  }

  expandedRowId?: string

  search: string

  filters: {
    roles: string[]
    status: string[]
  }

  cognitoInfoModal: {
    visible: boolean
    email: string
    data: CognitoUserInfo | null
    loading: boolean
  }

  sort: {
    field?: SortField
    order?: SortOrder
  }
}

type Action =
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse' }
  | { type: 'search/set'; payload: string }
  | { type: 'filters/set'; payload: Partial<State['filters']> }
  | { type: 'cognitoInfoModal/open'; payload: string }
  | { type: 'cognitoInfoModal/close' }
  | { type: 'cognitoInfoModal/setData'; payload: CognitoUserInfo | null }
  | { type: 'cognitoInfoModal/setLoading'; payload: boolean }
  | { type: 'pagination/currentPage/set'; payload: number }
  | { type: 'pagination/pageSize/set'; payload: number }
  | { type: 'sort/set'; payload: State['sort'] }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'row/expand': {
      return {
        ...state,
        expandedRowId: action.payload
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowId: undefined
      }
    }

    case 'search/set': {
      return {
        ...state,
        search: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        },
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'cognitoInfoModal/open': {
      return {
        ...state,
        cognitoInfoModal: {
          ...state.cognitoInfoModal,
          visible: true,
          email: action.payload
        }
      }
    }

    case 'cognitoInfoModal/close': {
      return {
        ...state,
        cognitoInfoModal: {
          ...state.cognitoInfoModal,
          visible: false,
          data: null,
          loading: false
        }
      }
    }

    case 'cognitoInfoModal/setData': {
      return {
        ...state,
        cognitoInfoModal: {
          ...state.cognitoInfoModal,
          data: action.payload
        }
      }
    }

    case 'cognitoInfoModal/setLoading': {
      return {
        ...state,
        cognitoInfoModal: {
          ...state.cognitoInfoModal,
          loading: action.payload
        }
      }
    }

    case 'pagination/currentPage/set': {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          currentPage: action.payload
        }
      }
    }

    case 'pagination/pageSize/set': {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          currentPage: 0,
          pageSize: action.payload
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        sort: action.payload
      }
    }

    default: {
      return state
    }
  }
}

export function UsersPage(): JSX.Element {
  const { inheritance } = useAppSelector(state => state.app.user)
  const [searchParams, setSearchParams] = useSearchParams({
    status: ['active']
  })
  const allowed = useCallback(
    (roles: Role[]) => {
      const set = new Set(inheritance)
      return roles.every(role => set.has(role.id))
    },
    [inheritance]
  )
  const [form] = Form.useForm<FormValues>()
  const { resetFields, setFieldsValue } = form

  const params = Object.fromEntries(searchParams.entries())
  const [state, dispatch] = useReducer(reducer, {
    search: params.search ?? '',
    filters: {
      roles: params.roles ? params.roles.split(',') : [],
      status: params.status ? params.status.split(',') : []
    },
    cognitoInfoModal: {
      visible: false,
      email: '',
      data: null,
      loading: false
    },
    pagination: {
      currentPage: 0,
      pageSize: 10
    },
    sort: {}
  })

  const [debouncedSearch, setDebouncedSearch] = useState(state.search)
  const [debouncedLoadingStatus, setDebouncedLoadingStatus] = useState(false)

  useEffect(() => {
    const payload: Record<string, string | undefined | boolean | string[]> = {
      search: state.search,
      status: state.filters.status,
      roles: state.filters.roles,
      page: state.pagination.currentPage.toString(),
      'page-size': state.pagination.pageSize.toString()
    }

    setSearchParams(
      new URLSearchParams(
        Object.keys(payload).reduce((acc, current) => {
          if (payload[current] === undefined) {
            return acc
          }
          if (Array.isArray(payload[current])) {
            return {
              ...acc,
              [current]: (payload[current] as unknown as string[]).join(',')
            }
          }
          return {
            ...acc,
            [current]: payload[current]
          }
        }, {})
      )
    )
  }, [
    state.search,
    state.filters.status,
    state.filters.roles,
    state.pagination.currentPage,
    state.pagination.pageSize,
    setSearchParams
  ])

  useDebounce(
    () => {
      setDebouncedSearch(state.search.toLowerCase().trim())
    },
    500,
    [state.search]
  )

  const query = useQuery<unknown, unknown, UsersResponse, QueryContextKey>({
    queryKey: ['users', debouncedSearch, state.filters, state.pagination, state.sort],

    async queryFn(context) {
      const [, search, filters, pagination, sort] = context.queryKey

      return UserApi.getUsers({
        roles: filters.roles,
        search,
        status: filters.status,
        page: pagination.currentPage.toString(),
        'page-size': pagination.pageSize.toString(),
        'order-by': sort.field,
        direction: sort.order
      })
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false
  })

  useDebounce(
    () => {
      setDebouncedLoadingStatus(query.isFetching)
    },
    query.isFetching ? 500 : 0,
    [query.isFetching]
  )

  const mutation = useMutation<void, MutationError, MutationPayload>({
    async mutationFn({ id, ...data }: MutationPayload) {
      const isNew = id === undefined

      await (isNew ? UserApi.createUser(data) : UserApi.updateUser(id!, data))

      dispatch({ type: 'row/collapse' })
      void query.refetch()
    }
  })

  const rolesQuery = useQuery<unknown, unknown, Role[]>({
    queryKey: ['roles'],

    async queryFn() {
      return UserApi.getRoles()
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      return UserApi.getLocations()
    }
  })

  const handleActivate = useCallback(
    (id: string) => {
      mutation.mutate({
        id,
        locked_since: null
      })
    },
    [mutation]
  )

  const handleDeactivate = useCallback(
    (id: string) => {
      mutation.mutate({
        id,
        locked_since: new Date().toISOString()
      })
    },
    [mutation]
  )

  const handleExpand = useCallback(
    (expanded: boolean, user: IUser) => {
      if (expanded) {
        dispatch({ type: 'row/expand', payload: user.id })
        setFieldsValue({
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          phone_number: user.phone_number ?? '',
          authentication_method: user.authentication_method,
          roles: user.roles.map(role => ({ label: role.name, value: role.id })),
          locations: user.locations.map(location => ({ label: location.name, value: location.id })),
          groups: user.groups.map(group => ({ label: group.name, value: group.id }))
        })
      } else {
        dispatch({ type: 'row/collapse' })
        resetFields()
      }
    },
    [setFieldsValue, resetFields]
  )

  const columns = useMemo<TableColumnsType<IUser>>(
    () => [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: '25%',
        className: 'show-default-sort',
        sorter: true,
        sortOrder:
          state.sort.field === 'email'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Phone',
        dataIndex: 'phone_number',
        key: 'phone_number',
        width: '15%',
        render(phone: IUser['phone_number']) {
          return phone ?? '—'
        }
      },
      {
        title: 'First Name',
        dataIndex: 'first_name',
        key: 'first_name',
        width: '10%',
        className: 'show-default-sort',
        sorter: true,
        sortOrder:
          state.sort.field === 'first_name'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Last Name',
        dataIndex: 'last_name',
        key: 'last_name',
        width: '10%',
        className: 'show-default-sort',
        sorter: true,
        sortOrder:
          state.sort.field === 'last_name'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Roles',
        dataIndex: 'roles',
        key: 'roles',
        width: '20%',
        filters:
          rolesQuery.data?.map(role => ({
            text: role.name,
            value: role.id
          })) ?? [],
        filteredValue: state.filters.roles,
        render(roles: Role[]) {
          return (
            <Space wrap direction='horizontal' size={[2, 6]}>
              {roles.map(role => (
                <Tag key={role.id} color={roleColorMap[role.id]}>
                  {role.name}
                </Tag>
              ))}
            </Space>
          )
        },
        className: 'show-default-sort',
        sorter: true,
        sortOrder:
          state.sort.field === 'roles'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Locations & Groups',
        dataIndex: 'locations',
        key: 'locations',
        // width: '15%',
        render(_, user) {
          return (
            <Space wrap direction='horizontal' size={[2, 6]}>
              {user.locations.map(location => (
                <Tag
                  key={location.id}
                  color={locationColorMapper[location.key]}
                  title={location.name}
                >
                  {location.short_name}
                </Tag>
              ))}

              {user.locations.length > 0 && user.groups.length > 0 && (
                <Divider type='vertical' className='mr-2 ml-0' />
              )}

              {user.groups.map(group => (
                <Tag
                  key={group.id}
                  color={
                    locationColorMapper[
                      locationsQuery.data?.find(l => l.id === group.location_id)?.key ?? ''
                    ]
                  }
                  title={group.name}
                >
                  {group.number}
                </Tag>
              ))}
            </Space>
          )
        }
      },
      {
        title: 'Status',
        key: 'status',
        width: '5.125rem',
        className: 'show-default-sort',
        filters: [
          {
            text: 'Locked',
            value: 'locked'
          },
          {
            text: 'Active',
            value: 'active'
          }
        ],
        filteredValue: state.filters.status,

        sorter: true,
        sortOrder:
          state.sort.field === 'status'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true,

        render(_, user) {
          const isLocked =
            typeof user.locked_since === 'string'
              ? new Date(user.locked_since) <= new Date()
              : false

          return (
            <div className='flex gap-3 justify-start items-center'>
              {isLocked ? 'Locked' : 'Active'}
            </div>
          )
        }
      },
      {
        key: 'actions',
        width: '5.5rem',
        render(_, user) {
          if (!allowed(user.roles)) {
            return null
          }
          const isDisabled = state.expandedRowId === user.id
          const isLocked = typeof user.locked_since === 'string'

          return (
            <div className='flex justify-center'>
              <Dropdown
                disabled={isDisabled}
                menu={{
                  items: [
                    {
                      key: 'edit',
                      label: 'Edit',
                      onClick() {
                        handleExpand(true, user)
                      },
                      icon: <EditOutlined />
                    },
                    {
                      type: 'divider'
                    },
                    {
                      key: isLocked ? 'activate' : 'deactivate',

                      label: isLocked ? 'Activate' : 'Deactivate',

                      danger: !isLocked,
                      onClick() {
                        if (isLocked) {
                          handleActivate(user.id)
                          return
                        }

                        handleDeactivate(user.id)
                      },
                      icon: isLocked ? <UnlockOutlined /> : <LockOutlined />
                    }
                  ]
                }}
              >
                <Button
                  htmlType='button'
                  size='large'
                  type='text'
                  icon={<EllipsisOutlined />}
                  className={cx({
                    'opacity-25 cursor-not-allowed': isDisabled
                  })}
                />
              </Dropdown>
            </div>
          )
        }
      }
    ],
    [
      handleActivate,
      handleDeactivate,
      handleExpand,
      rolesQuery.data,
      locationsQuery.data,
      state.expandedRowId,
      allowed,
      state.filters,
      state.sort
    ]
  )

  const handleSave = useCallback(
    (id: string | undefined, values: FormValues) => {
      const normalizedPhoneNumber = values.phone_number?.trim() ?? ''
      const authenticationMethod = values.authentication_method ?? 'passkey'

      mutation.mutate({
        id,
        email: values.email ?? '',
        first_name: values.first_name ?? '',
        last_name: values.last_name ?? '',
        phone_number: normalizedPhoneNumber.length > 0 ? normalizedPhoneNumber : null,
        authentication_method: authenticationMethod,
        roles: values.roles?.map(role => role.value) ?? [],
        locations: values.locations?.map(location => location.value) ?? [],
        groups: values.groups?.map(group => group.value) ?? []
      })
    },
    [mutation]
  )

  const handleCancel = useCallback(() => {
    dispatch({ type: 'row/collapse' })
    resetFields()
  }, [resetFields])

  return (
    <Space direction='vertical' size={24} className='p-0 w-full'>
      {state.expandedRowId === 'new' && (
        <Card className='w-full rounded-xl' title='New User'>
          <UserForm
            form={form}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={mutation.isLoading}
            error={mutation.error as MutationError}
          />
        </Card>
      )}

      <Card
        className='w-full rounded-xl [&>.ant-card-body]:p-0 -mt-4'
        title='Users'
        extra={
          <Dropdown
            placement='bottomRight'
            menu={{
              items: [
                {
                  key: 'add-user',
                  label: 'Add User',
                  onClick() {
                    resetFields()
                    dispatch({ type: 'row/expand', payload: 'new' })
                  },
                  icon: <UserAddOutlined />
                }
              ]
            }}
          >
            <Button
              htmlType='button'
              size='large'
              type='text'
              className='flex justify-center items-center -my-4'
              icon={<EllipsisOutlined />}
            />
          </Dropdown>
        }
      >
        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search by email, first name or last name'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    state.search.length > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'search/set',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.search}
                  onChange={event => {
                    dispatch({
                      type: 'search/set',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                <Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  <span className='inline-flex items-center space-x-2'>
                    <SyncOutlined
                      className={cx(
                        'text-base',
                        query.isFetching
                          ? 'animate-custom-spin text-lucent-gray'
                          : 'text-lucent-primary'
                      )}
                    />
                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase relative top-[-2px]',
                        query.isFetching ? 'text-lucent-gray' : 'text-lucent-primary'
                      )}
                    >
                      Refresh
                    </span>
                  </span>
                </Button>
              </Space>
            </div>
          </div>
          <Table
            size='small'
            rowKey='id'
            dataSource={query.data?.data}
            loading={debouncedLoadingStatus}
            columns={columns}
            expandable={{
              rowExpandable: user => allowed(user.roles),
              onExpand: handleExpand,
              expandedRowKeys: typeof state.expandedRowId === 'string' ? [state.expandedRowId] : [],

              expandedRowRender(user, _, __, expanded) {
                if (!expanded) {
                  return null
                }

                return (
                  <UserForm
                    id={user.id}
                    email={user.email}
                    form={form}
                    className='p-6'
                    onSave={handleSave}
                    onCancel={handleCancel}
                    loading={mutation.isLoading}
                    error={mutation.error as MutationError}
                  />
                )
              },

              expandIcon: ExpandIcon,
              columnWidth: '2rem'
            }}
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: query.data?.total ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (typeof query.data?.total === 'number' && query.data.total !== total) {
                  parts.push(
                    `Total ${query.data.total} ${query.data.total === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (extra.action === 'filter') {
                const payload = {
                  roles: filters.roles?.map(value => value as string) ?? [],
                  status: filters.status?.map(value => value as string) ?? []
                }

                dispatch({
                  type: 'filters/set',
                  payload
                })
              }

              if (extra.action === 'paginate') {
                dispatch({
                  type: 'pagination/currentPage/set',
                  payload: (pagination.current ?? 1) - 1
                })

                if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                  dispatch({
                    type: 'pagination/pageSize/set',
                    payload: pagination.pageSize ?? 10
                  })
                }
              }

              if (extra.action === 'sort') {
                const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

                dispatch({
                  type: 'sort/set',
                  payload: {
                    field: singleSorter?.column?.key as SortField | undefined,
                    order:
                      typeof singleSorter?.order === 'string'
                        ? ({ ascend: 'asc', descend: 'desc' }[singleSorter.order] as SortOrder)
                        : undefined
                  }
                })
              }
            }}
          />
        </div>
      </Card>
    </Space>
  )
}

interface UserFormProps {
  id?: string
  email?: string
  form: FormInstance<FormValues>
  className?: string
  onSave: (id: string | undefined, values: FormValues) => void
  onCancel: () => void
  loading: boolean
  error?: MutationError
}

function formatEventLabel(value: string): string {
  return value.replaceAll(/([a-z])([A-Z])/g, '$1 $2').replace(/^SignIn$/, 'Sign-in')
}

function UserForm({
  id,
  form,
  className,
  onSave,
  onCancel,
  loading,
  error
}: UserFormProps): JSX.Element {
  const { inheritance } = useAppSelector(state => state.app.user)
  const set = new Set(inheritance)
  const allowed = (role: Role) => set.has(role.id)

  const isNew = id === undefined
  const phoneNumberValue = Form.useWatch('phone_number', form)
  const trimmedPhoneNumber = (phoneNumberValue ?? '').trim()
  const authenticationMethodValue = Form.useWatch('authentication_method', form)
  const selectedAuthMethod = (authenticationMethodValue ??
    'passkey') as IUser['authentication_method']
  const canSelectMfa = trimmedPhoneNumber.length > 0

  const [groupsSearchValue, setGroupsSearchValue] = useState('')
  const [debouncedGroupsSearchValue, setDebouncedGroupsSearchValue] = useState(groupsSearchValue)

  const [cognitoInfo, setCognitoInfo] = useState<CognitoUserInfo | null>(null)
  const [cognitoLoading, setCognitoLoading] = useState(false)
  const [showCognitoInfo, setShowCognitoInfo] = useState(false)

  interface GroupsQueryData {
    groups: Group[]
    totalFiltered: number
    totalUnfiltered: number
  }

  type QueryContextKey = ['groups', string]

  const groupsQuery = useQuery<unknown, unknown, GroupsQueryData, QueryContextKey>({
    queryKey: ['groups', debouncedGroupsSearchValue],

    async queryFn(context) {
      const [, query = ''] = context.queryKey

      const searchFields = ['name', 'number']

      const sp = new URLSearchParams([
        ['page-size', '10'],
        ...(query.length > 0
          ? [['query', query], ...searchFields.map(name => ['search-fields', name])]
          : [])
      ])

      const { data } = await $api.get(`/groups?${sp.toString()}`)

      return {
        groups: data.rows,
        totalFiltered: data.total_filtered,
        totalUnfiltered: data.total_unfiltered
      }
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      return UserApi.getLocations()
    }
  })

  const rolesQuery = useQuery<unknown, unknown, Role[]>({
    queryKey: ['roles'],

    async queryFn() {
      return UserApi.getRoles()
    }
  })

  const refreshCognitoInfo = useCallback(async () => {
    if (!id) {
      return
    }

    setCognitoLoading(true)
    try {
      const data = await UserApi.getUserCognitoInfo(id)
      setCognitoInfo(data)
    } catch (err) {
      console.error(err)
      setCognitoInfo(null)
    } finally {
      setCognitoLoading(false)
    }
  }, [id])

  const passwordMutation = useMutation<void, MutationError, { id: string; resend: boolean }>({
    async mutationFn({ id, resend }: { id: string; resend: boolean }) {
      await (resend ? UserApi.resendPassword(id) : UserApi.resetPassword(id))
      await refreshCognitoInfo()
    }
  })

  useEffect(() => {
    if (!id) {
      setCognitoInfo(null)
      setShowCognitoInfo(false)
      return
    }

    void refreshCognitoInfo()
  }, [id, refreshCognitoInfo])

  useEffect(() => {
    if (isNew && !authenticationMethodValue) {
      form.setFieldsValue({ authentication_method: 'passkey' })
    }
  }, [authenticationMethodValue, form, isNew])

  useDebounce(
    () => {
      setDebouncedGroupsSearchValue(groupsSearchValue.trim().toLowerCase())
    },
    500,
    [groupsSearchValue]
  )

  const validationError = error instanceof ValidationError ? error : undefined
  const generalError = error && !(error instanceof ValidationError) ? error : undefined

  return (
    <Form
      form={form}
      size='large'
      layout='horizontal'
      className={className}
      colon={false}
      labelWrap
      labelCol={{
        flex: '0 0 8rem',
        className: 'font-semibold'
      }}
      initialValues={{
        authentication_method: 'passkey'
      }}
      onFinish={values => {
        onSave(id, values)
      }}
    >
      {generalError && (
        <Alert
          type='error'
          showIcon
          message={generalError.message || 'Failed to create user'}
          className='mb-4'
        />
      )}
      <Form.Item
        name='email'
        label='Email Address'
        required
        validateStatus={validationError?.issues?.email ? 'error' : undefined}
        help={validationError?.issues?.email}
      >
        <Input type='email' placeholder='some-user@example.org' disabled={!isNew} />
      </Form.Item>

      <Form.Item name='first_name' label='First Name' required>
        <Input placeholder='Matthew' />
      </Form.Item>

      <Form.Item name='last_name' label='Last Name' required>
        <Input placeholder='Harrison' />
      </Form.Item>

      <Form.Item
        name='phone_number'
        label='Phone Number'
        validateStatus={validationError?.issues?.phone_number ? 'error' : undefined}
        help={validationError?.issues?.phone_number}
        rules={[
          {
            validator(_, value) {
              const trimmed = (value ?? '').trim()
              if (!trimmed) {
                return Promise.resolve()
              }

              if (!/^\+?[1-9]\d{9,14}$/.test(trimmed)) {
                return Promise.reject(new Error('Use E.164 format, e.g. +14155552671'))
              }

              return Promise.resolve()
            }
          }
        ]}
      >
        <Input placeholder='+15551234567' type='tel' />
      </Form.Item>
      {!isNew && (
        <div className='flex ml-32 gap-2 items-center mb-6 -mt-4 text-xs text-gray-600'>
          <span>Phone verification:</span>
          {cognitoLoading ? (
            <Spin size='small' />
          ) : (
            <Tag
              color={
                cognitoInfo?.accountStatus.phoneVerified === true
                  ? 'green'
                  : cognitoInfo?.accountStatus.phoneVerified === false
                    ? 'red'
                    : undefined
              }
            >
              {cognitoInfo?.accountStatus.phoneVerified === undefined
                ? 'Unknown'
                : cognitoInfo.accountStatus.phoneVerified
                  ? 'Verified'
                  : 'Not verified'}
            </Tag>
          )}
        </div>
      )}

      <Form.Item
        name='authentication_method'
        label='MFA Type'
        dependencies={['phone_number']}
        rules={[
          { required: true, message: 'Select an authentication method' },
          () => ({
            validator(_, value) {
              if (value === 'sms_mfa' && trimmedPhoneNumber.length === 0) {
                return Promise.reject(new Error(MFA_PHONE_ERROR_MESSAGE))
              }

              return Promise.resolve()
            }
          })
        ]}
        validateStatus={validationError?.issues?.authentication_method ? 'error' : undefined}
        help={validationError?.issues?.authentication_method}
      >
        <AuthenticationMethodSwitch
          value={selectedAuthMethod}
          onChange={value => {
            form.setFieldsValue({ authentication_method: value })
          }}
          canSelectMfa={canSelectMfa || selectedAuthMethod === 'sms_mfa'}
        />
      </Form.Item>

      <Form.Item
        name='roles'
        label='Roles'
        tooltip='Roles are presets of permissions. A user can have multiple roles.'
      >
        <Select
          mode='multiple'
          placeholder='Select roles'
          labelInValue
          loading={rolesQuery.isLoading}
          options={rolesQuery.data
            ?.filter(role => allowed(role))
            .map(role => ({
              label: role.name,
              value: role.id
            }))}
        />
      </Form.Item>

      <Form.Item
        name='locations'
        label='Locations'
        tooltip='A user can be assigned to multiple locations'
      >
        <Select
          mode='multiple'
          placeholder='Select locations'
          labelInValue
          loading={locationsQuery.isLoading}
          options={locationsQuery.data?.map(location => ({
            label: location.name,
            value: location.id
          }))}
        />
      </Form.Item>

      <Form.Item
        name='groups'
        label='Groups'
        tooltip='Apart from location, a user can be assigned to multiple individual groups'
      >
        <Select
          mode='multiple'
          placeholder='Start typing to search groups'
          filterOption={false}
          labelInValue
          notFoundContent={
            groupsQuery.isLoading ? (
              <div className='w-full h-[5.075rem] flex items-center justify-center'>
                <Spin />
              </div>
            ) : undefined
          }
          optionLabelProp='number'
          options={groupsQuery.data?.groups.reduce<
            Array<{
              label: string
              options: Array<{ label: React.ReactNode; number: string; value: string }>
            }>
          >(
            (acc, group) => {
              const location = locationsQuery.data?.find(l => l.id === group.location_id)
              if (!location) return acc

              let category = acc.find(c => c.label === location.name)

              if (!category) {
                category = { label: location.name, options: [] }
                acc.push(category)
              }

              category.options.push({
                label: (
                  <span>
                    <span className='font-semibold'>{group.number}</span> {group.name}
                  </span>
                ),
                number: group.number,
                value: group.id
              })

              return acc
            },
            [] as Array<{
              label: string
              options: Array<{ label: React.ReactNode; number: string; value: string }>
            }>
          )}
          searchValue={groupsSearchValue}
          onSearch={value => {
            setGroupsSearchValue(value)
          }}
          onSelect={() => {
            setGroupsSearchValue('')
          }}
          loading={groupsQuery.isLoading}
        />
      </Form.Item>

      <div className='flex justify-end'>
        <div
          style={{
            flexGrow: '1',
            color: '#979797',
            paddingTop: '10px'
          }}
        >
          <div className='flex gap-2 items-center'>
            <span>User ID: {id}</span>
          </div>
        </div>
        <Space direction='horizontal' size={12}>
          <Button
            onClick={() => {
              onCancel()
            }}
          >
            Cancel
          </Button>

          <Button type='primary' htmlType='submit' loading={loading}>
            Save
          </Button>
        </Space>
      </div>

      <div className='mt-4'>
        {id && (
          <>
            <Button
              type='text'
              size='small'
              className='flex gap-1 items-center p-0 h-auto'
              onClick={async () => {
                if (showCognitoInfo) {
                  setShowCognitoInfo(false)
                  return
                }

                setShowCognitoInfo(true)
                await refreshCognitoInfo()
              }}
            >
              <span
                className={`transition-transform duration-300 ${
                  showCognitoInfo ? 'rotate-90' : ''
                }`}
              >
                <CaretRightOutlined />
              </span>
              <span className='text-zink-500'>Cognito Information</span>
            </Button>

            {showCognitoInfo && cognitoLoading && (
              <div className='mt-4'>
                <Spin size='small' />
              </div>
            )}

            {showCognitoInfo && !cognitoLoading && cognitoInfo && (
              <div className='mt-6 space-y-6'>
                {/* --- USER INFO CARD --- */}
                <div className='p-4 bg-white rounded-xl border shadow-sm'>
                  <div className='mb-2 text-lg font-semibold'>User information</div>
                  <div className='grid grid-cols-1 gap-6 text-sm md:grid-cols-3 md:divide-x md:divide-gray-300'>
                    {/* Left */}
                    <div className='pr-4 space-y-1'>
                      <div>
                        <strong>User ID (Sub):</strong>
                        <br />
                        {cognitoInfo.sub}
                      </div>
                      <div>
                        <strong>Alias attributes used to sign in:</strong>
                        <br />
                        User name
                      </div>
                      <div>
                        <strong>MFA setting:</strong>
                        <br />
                        {cognitoInfo.mfaEnabled ? 'MFA active' : 'MFA inactive'}
                      </div>
                      <div>
                        <strong>MFA methods:</strong>
                        <br />
                        {cognitoInfo.mfaMethods.length > 0
                          ? cognitoInfo.mfaMethods.map(method => method.name).join(', ')
                          : '-'}
                      </div>
                      <div>
                        <strong>Phone verification:</strong>
                        <br />
                        <Tag color={cognitoInfo.accountStatus.phoneVerified ? 'green' : 'red'}>
                          {cognitoInfo.accountStatus.phoneVerified ? 'Verified' : 'Not verified'}
                        </Tag>
                      </div>

                      <div className='text-xs text-gray-500'>
                        Authentication method is managed in the form above.
                      </div>
                    </div>

                    {/* Middle */}
                    <div className='px-4 space-y-1'>
                      <div className='flex gap-2 items-center'>
                        <strong>Account status:</strong>
                        {cognitoInfo.status.enabled ? (
                          <span className='font-semibold text-green-600'>Enabled</span>
                        ) : (
                          <span className='font-semibold text-red-500'>Disabled</span>
                        )}
                      </div>
                      <div className='flex gap-2 items-center'>
                        <strong>Confirmation status:</strong>
                        <span className='bg-green-600 text-white px-2 py-0.5 rounded text-xs'>
                          {cognitoInfo.status.userStatus}
                        </span>
                      </div>
                      {cognitoInfo.status.enabled &&
                        ['CONFIRMED', 'FORCE_CHANGE_PASSWORD'].includes(
                          cognitoInfo.status.userStatus
                        ) && (
                          <Button
                            className='mt-10'
                            type='primary'
                            loading={passwordMutation.isLoading}
                            onClick={() => {
                              passwordMutation.mutate({
                                id,
                                resend: cognitoInfo.status.userStatus === 'FORCE_CHANGE_PASSWORD'
                              })
                            }}
                          >
                            {cognitoInfo.status.userStatus === 'CONFIRMED' ? 'Reset' : 'Resend'}{' '}
                            Password
                          </Button>
                        )}
                    </div>

                    {/* Right */}
                    {/* Right */}
                    <div className='pl-4 space-y-1'>
                      <div>
                        <strong>Created time:</strong>
                        <br />
                        {cognitoInfo.status.userCreateDate
                          ? new Date(cognitoInfo.status.userCreateDate)
                              .toLocaleString('en-US', {
                                timeZone: 'America/Chicago',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZoneName: 'short'
                              })
                              .replace(' 2025,', ', 2025')
                          : 'N/A'}
                      </div>
                      <div>
                        <strong>Last updated time:</strong>
                        <br />
                        {cognitoInfo.status.userLastModifiedDate
                          ? new Date(cognitoInfo.status.userLastModifiedDate)
                              .toLocaleString('en-US', {
                                timeZone: 'America/Chicago',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZoneName: 'short'
                              })
                              .replace(' 2025,', ', 2025')
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- EVENT HISTORY TABLE --- */}
                <div className='p-4 bg-white rounded-xl border shadow-sm'>
                  <div className='flex justify-between items-center text-lg font-semibold'>
                    <span>User event history ({cognitoInfo.authenticationEvents.length})</span>
                  </div>

                  {cognitoInfo.authenticationEvents.length === 0 ? (
                    <div className='text-sm text-gray-500'>No events found</div>
                  ) : (
                    <div className='overflow-x-auto'>
                      <table className='mt-2 min-w-full text-sm'>
                        <thead className='text-left'>
                          <tr className='text-gray-600 [&>th]:py-2 [&>th]:px-3 [&>th]:border-b [&>th]:border-gray-300'>
                            <th>Sign-in time</th>
                            <th>Event</th>
                            <th>Result</th>
                            <th>Risk level</th>
                            <th>Risk decision</th>
                            <th>Challenge</th>
                            <th>Device</th>
                            <th>IP address</th>
                            <th>Location (city)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cognitoInfo.authenticationEvents.map((event, index) => (
                            <tr key={index} className='border-b'>
                              {/* <td className='px-3 py-2'>
                                  {new Date(event.creationDate).toLocaleString('en-US', {
                                    timeZone: 'Europe/Moscow',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  }).replace(' 2025,', ', 2025')}
                                </td> */}

                              <td className='px-3 py-2'>
                                {new Date(event.creationDate)
                                  .toLocaleString('en-US', {
                                    timeZone: 'America/Chicago',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                    timeZoneName: 'short'
                                  })
                                  .replace(' 2025,', ', 2025')}
                              </td>

                              <td className='px-3 py-2'>{formatEventLabel(event.eventType)}</td>
                              <td className='px-3 py-2'>{event.eventResponse}</td>
                              <td className='px-3 py-2'>
                                {formatEventLabel(event.eventRisk ?? '-')}
                              </td>
                              <td className='px-3 py-2'>{event.riskDecision ?? '-'}</td>
                              <td className='px-3 py-2'>
                                {Object.keys(event.challengeResponses ?? {}).length > 0
                                  ? Object.entries(event.challengeResponses ?? {})
                                      .map(([k, v]) => `${k}:${v as unknown as string}`)
                                      .join(', ')
                                  : '-'}
                              </td>
                              <td className='px-3 py-2'>
                                {event.eventContextData?.deviceName ?? '-'}
                              </td>
                              <td className='px-3 py-2'>
                                {event.eventContextData?.ipAddress ?? '-'}
                              </td>
                              <td className='px-3 py-2'>{event.eventContextData?.city ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* <div className='overflow-x-auto max-w-full bg-white rounded-xl border shadow-sm'>
                  {cognitoInfo && (
                    <pre className='p-4 mt-2 text-xs whitespace-pre-wrap break-words bg-white rounded'>
                      {JSON.stringify(cognitoInfo, null, 2)}
                    </pre>
                  )}
                </div> */}
              </div>
            )}
            {showCognitoInfo && !cognitoLoading && !cognitoInfo && (
              <div className='flex gap-2 items-center p-4 mt-4 text-sm text-gray-500 bg-white rounded-xl border shadow-sm'>
                <WarningOutlined className='text-base text-yellow-500' />
                No Cognito data available for this user
              </div>
            )}
          </>
        )}
      </div>

      {/* {cognitoInfo && (
              <pre className='overflow-auto p-2 mt-2 max-h-60 text-xs bg-gray-100 rounded'>
                {JSON.stringify(cognitoInfo, null, 2)}
              </pre>
            )} */}
    </Form>
  )
}

interface AuthenticationMethodSwitchProps {
  value?: IUser['authentication_method']
  onChange?: (value: IUser['authentication_method']) => void
  canSelectMfa: boolean
}

function AuthenticationMethodSwitch({
  value = 'passkey',
  onChange,
  canSelectMfa
}: AuthenticationMethodSwitchProps): JSX.Element {
  const smsSelected = value === 'sms_mfa'
  const passkeySelected = !smsSelected
  const smsDisabled = !canSelectMfa && !smsSelected

  return (
    <div className='flex flex-col gap-3'>
      <button
        type='button'
        className={cx(
          'w-full text-left p-3 rounded-lg border-solid border transition-colors focus:outline-none',
          passkeySelected
            ? 'border-lucent-primary bg-lucent-primary/5 shadow-sm'
            : 'border-gray-200 hover:border-lucent-primary/70'
        )}
        onClick={() => {
          if (!passkeySelected) {
            onChange?.('passkey')
          }
        }}
      >
        <div className='flex items-center justify-between'>
          <div>
            <div className='font-semibold'>Passkey &amp; UbiKey</div>
            <div className='text-xs text-gray-500'>Passwordless login using WebAuthn keys.</div>
          </div>
          {passkeySelected && <Tag color='blue'>Selected</Tag>}
        </div>
      </button>

      <button
        type='button'
        className={cx(
          'w-full text-left p-3 rounded-lg border-solid border transition-colors focus:outline-none',
          smsSelected
            ? 'border-lucent-primary bg-lucent-primary/5 shadow-sm'
            : 'border-gray-200 hover:border-lucent-primary/70',
          smsDisabled && 'cursor-not-allowed opacity-60 hover:border-gray-200'
        )}
        disabled={smsDisabled}
        onClick={() => {
          if (smsDisabled || smsSelected) {
            return
          }
          onChange?.('sms_mfa')
        }}
      >
        <div className='flex items-center justify-between'>
          <div>
            <div className='font-semibold'>MFA (SMS via Cognito)</div>
            <div className='text-xs text-gray-500'>
              Send verification codes to the stored phone number.
            </div>
          </div>
          {smsSelected && <Tag color='blue'>Selected</Tag>}
        </div>

        {smsDisabled && (
          <span className='text-[11px] text-gray-500 block mt-2'>
            Add a phone number to enable SMS MFA.
          </span>
        )}
      </button>
    </div>
  )
}

function ExpandIcon(props: RenderExpandIconProps<IUser>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-30 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
